import React, { useMemo, useState } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { Alert, Button } from 'reactstrap'
import moment from 'moment'

import HistoryTable from '../Material/HistoryTable'
import DateRangeControl from './DateRangeControl'
import { FilterToken } from './ReportToolbar'
import { daysBetween, rankKeys, totalsBy } from './activityReport'
import { actionMetaFor } from './reportRegistry'

// Same item shape `bqHistory` returns (see HistoryTable.js/HistoryList.js) --
// the backend serves this off the same BQVersionNode, so the frontend table
// is shared rather than growing a second one.
export const ACTIVITY_LOGS = gql`query ActivityLogs($report: String!, $userId: Int, $noActor: Boolean, $actions: [String!], $startDate: Date!, $endDate: Date!, $offset: Int) {
    activityLogs(report: $report, userId: $userId, noActor: $noActor, actions: $actions, startDate: $startDate, endDate: $endDate, offset: $offset) {
        totalCount
        pageSize
        items {
            appLabel
            modelName
            objectId
            eventTimestamp
            actorName
            isNew
            isDeleted
            snapshot
            dirtyFields
            comment
        }
    }
}`

const RANGE_LIMIT_DAYS = 90

// The number of quick-pick chips offered before any fetch -- see "Busiest in
// this range" in the pre-fetch stage below.
const QUICKPICK_COUNT = 3

// The query's XOR key for "which actor": a real user's id, or the No Actor
// sentinel. Mirrors `SYSTEM_EXCLUDE_VALUE`'s role in activityReport.js, but
// this picker's own convention (`u:<id>` / `none`) rather than that one's,
// because this value round-trips into `userId`/`noActor` query variables
// rather than into `filterRows`.
const actorKeyFor = (row) => (row.userId === null ? 'none' : `u:${row.userId}`)

/**
 * Every distinct actor with activity on this report in range, ranked by total
 * activity -- built from the UNFILTERED rows the hook returns, so the Role
 * filter (a display concern on the chart/table tabs) never narrows who can be
 * looked up here. `actionMeta` (from `actionMetaFor(report)`) gives
 * `totalsBy` the per-action metric to sum, the same way
 * `useActivityReport`'s own `userOptions`/`excludeUserOptions` do.
 *
 * One option per distinct `userId` (or the No Actor sentinel), labelled with
 * the row's already-normalised `userName` -- "No Actor", "Customer #<id>", or
 * a real name -- exactly as `useActivityReport`'s `resolveUserName` set it.
 */
export const actorOptionsFor = (rows, actionMeta) => {
    const labelByKey = {}
    const infoByKey = {}
    rows.forEach((row) => {
        const key = actorKeyFor(row)
        if (!(key in labelByKey)) {
            labelByKey[key] = row.userName
            infoByKey[key] = { userId: row.userId, noActor: row.userId === null }
        }
    })
    return rankKeys(totalsBy(rows, actorKeyFor, actionMeta)).map((key) => ({
        value: key,
        label: labelByKey[key],
        userId: infoByKey[key].userId,
        noActor: infoByKey[key].noActor,
    }))
}

// The two `FilterToken` kinds this row offers, alongside the same
// `DateRangeControl` every other tab's toolbar uses -- see ReportToolbar.js's
// own `BASE_KINDS` for the shape. Neither is drawn from that list: `actions`
// there is multi-select against a fixed catalog of every report's actions,
// while this one's options are `report.actions` scoped the same way, but the
// real difference is `user`, which has no BASE_KINDS equivalent at all --
// ReportToolbar's `users`/`excludeUsers` kinds narrow a chart/table view,
// while this is the one actor the drill-down below runs against, so it is
// single-select (`isMulti={false}` where this token is used) rather than a
// display filter.
const USER_KIND = { key: 'user', label: 'User', placeholder: 'Select a user' }
const ACTIONS_KIND = { key: 'actions', label: 'Actions', placeholder: 'All actions' }

const sortedActionValues = (actions) => actions.map((action) => action.value).sort()

/** The `{actorValue, actionValues, startDate, endDate}` snapshot last fetched, vs. now. */
const keysEqual = (a, b) => (
    !!a && !!b
    && a.actorValue === b.actorValue
    && a.startDate === b.startDate
    && a.endDate === b.endDate
    && a.actionValues.length === b.actionValues.length
    && a.actionValues.every((value, index) => value === b.actionValues[index])
)

/**
 * The Investigate tab: a one-actor lookup over a report's raw history rows,
 * a level below the charts/table views on the other tabs. Nothing is queried
 * until Fetch is pressed -- unlike the rest of the panel, this is a deliberate
 * per-actor drill-down, not something to run automatically on every filter
 * tweak.
 *
 * The filter row is a plain `.reports-toolbar` -- the same DateRangeControl
 * and the same `FilterToken` pills the other tabs' `ReportToolbar` renders
 * (see ReportToolbar.js) -- with a Fetch button (or the 90-day note in its
 * place) tacked on at the end, rather than the bespoke query bar this tab
 * used to have; a previous version of this tab's own three-cell bar was
 * rejected for looking like a different control from the rest of the panel.
 * Below that row sits whatever the current moment calls for: a "nothing
 * fetched yet" stage, a scrolling results box, or a "zero rows" stage.
 * `onDateChange` is the same setter `ReportsPanel` hands its `ReportToolbar`
 * -- this tab hosts its own `DateRangeControl` instance rather than sharing
 * the toolbar's, since the toolbar itself is not rendered while Investigate
 * is active (see `ReportsPanel.js`).
 */
function InvestigateTab({ report, rows, startDate, endDate, onDateChange, canViewAllUsers, currentUserId }) {
    const actionMeta = useMemo(() => actionMetaFor(report), [report])
    const allActorOptions = useMemo(() => actorOptionsFor(rows, actionMeta), [rows, actionMeta])
    // A self-scoped viewer (no view-all permission) can only ever look
    // themselves up -- this narrows the picker rather than trusting the
    // backend alone to reject a foreign id, so the UI never offers a choice
    // it would then refuse. `currentUserId` is `null` while the catalog is
    // still loading (or, degenerately, if the cache ever served
    // `currentUser` without a `pk`); the No Actor option's own `userId` is
    // also `null`, so without the explicit `currentUserId !== null` guard
    // this would offer No Actor -- exactly the option the backend refuses a
    // self-scoped viewer -- and hide their own row. While it is null, the
    // picker is empty and the pre-fetch stage's self-scoped copy covers the
    // gap.
    const actorOptions = canViewAllUsers
        ? allActorOptions
        : allActorOptions.filter((option) => currentUserId !== null && option.userId === currentUserId)

    const [actor, setActor] = useState(null)
    const [selectedActions, setSelectedActions] = useState([])
    const [fetchKey, setFetchKey] = useState(null)
    const [items, setItems] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize, setPageSize] = useState(0)

    const [fetchLogs, { loading, error }] = useLazyQuery(ACTIVITY_LOGS, { fetchPolicy: 'network-only' })

    const rangeTooLong = daysBetween(startDate, endDate).length > RANGE_LIMIT_DAYS

    const currentKey = actor ? {
        actorValue: actor.value,
        actionValues: sortedActionValues(selectedActions),
        startDate,
        endDate,
    } : null
    // Stale as soon as any of the four inputs drift from what was last
    // fetched -- deliberately not a silent refetch: a lookup like this
    // should never show a different actor's rows under a header that still
    // names the old one. The stale banner below is the one place that gets
    // to say so; the results themselves stay on screen, just dimmed (see
    // `.reports-investigate-results--stale` in Reports.css).
    const isStale = !!fetchKey && !keysEqual(fetchKey, currentKey)

    const buildVariables = (offset) => ({
        report: report.id,
        userId: actor.noActor ? null : actor.userId,
        noActor: actor.noActor ? true : null,
        actions: selectedActions.length ? selectedActions.map((action) => action.value) : null,
        startDate,
        endDate,
        offset,
    })

    const runFetch = async (offset, append) => {
        const key = currentKey
        const result = await fetchLogs({ variables: buildVariables(offset) })
        const payload = result?.data?.activityLogs
        // On error `data` comes back undefined -- `error` above (reactive off
        // the same lazy query) is what renders the alert; nothing here to
        // append or count.
        if (!payload) return
        setItems((previous) => (append ? [...previous, ...payload.items] : payload.items))
        setTotalCount(payload.totalCount)
        setPageSize(payload.pageSize)
        setFetchKey(key)
    }

    const handleFetch = () => {
        if (!actor) return
        runFetch(0, false)
    }

    const handleLoadMore = () => {
        runFetch(items.length, true)
    }

    const handleClearActions = () => setSelectedActions([])

    const actorLabel = actor?.label
    const hasFetched = !!fetchKey
    const showLoadMore = hasFetched && items.length < totalCount
    // Top of `actorOptions` (already ranked by `actorOptionsFor`, and
    // already narrowed to the viewer's own row when self-scoped) -- when
    // there's nothing to rank (the self-scoped-empty case below) this is
    // simply empty, which is what keeps the quickpicks row from rendering.
    const quickpicks = actorOptions.slice(0, QUICKPICK_COUNT)
    const isSelfScopedEmpty = !canViewAllUsers && actorOptions.length === 0
    const hasSelectedActions = selectedActions.length > 0

    return (
        <div className="reports-investigate">
            {error && <Alert color="danger">{error.message}</Alert>}

            <div className="reports-toolbar reports-investigate-toolbar">
                <DateRangeControl
                    id="investigateDateRange"
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={onDateChange}
                />
                <FilterToken
                    kind={USER_KIND}
                    values={actor ? [actor] : []}
                    options={actorOptions}
                    onChange={(value) => setActor(value[0] || null)}
                    onRemove={() => setActor(null)}
                    isMulti={false}
                />
                <FilterToken
                    kind={ACTIONS_KIND}
                    values={selectedActions}
                    options={report.actions}
                    onChange={setSelectedActions}
                    onRemove={handleClearActions}
                />
                {rangeTooLong ? (
                    <span
                        className="reports-investigate-range-warning"
                        title="The history query allows at most 90 days at a time"
                    >
                        <i className="fa fa-exclamation-circle" aria-hidden="true"></i> Narrow to 90 days
                    </span>
                ) : (
                    <Button
                        className="reports-investigate-fetch"
                        color="primary"
                        disabled={!actor || loading}
                        onClick={handleFetch}
                    >
                        {loading ? 'Fetching…' : 'Fetch logs'}
                    </Button>
                )}
            </div>

            {fetchKey === null && (
                <div className="reports-investigate-stage">
                    <div className="reports-investigate-stage-icon">
                        <i className="fa fa-history" aria-hidden="true"></i>
                    </div>
                    <p className="reports-investigate-stage-title">Pick a user to trace their activity</p>
                    <p className="reports-investigate-stage-text">
                        {isSelfScopedEmpty
                            ? 'No activity recorded for you on this report in this range.'
                            : 'Every create and edit they logged on this report, newest first — the rows behind the numbers on the other tabs.'}
                    </p>
                    {quickpicks.length > 0 && (
                        <div className="reports-investigate-quickpicks">
                            <span className="reports-investigate-eyebrow">Busiest in this range</span>
                            {quickpicks.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className="reports-investigate-chip"
                                    onClick={() => setActor(option)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {hasFetched && isStale && (
                <div className="reports-investigate-stale">
                    Selection changed — fetch again to refresh
                    <button type="button" className="reports-investigate-stale-fetch" onClick={handleFetch}>
                        Fetch logs
                    </button>
                </div>
            )}

            {hasFetched && !isStale && totalCount > 0 && (
                <p className="reports-investigate-status">
                    {showLoadMore
                        ? `Showing ${items.length.toLocaleString()} of ${totalCount.toLocaleString()}`
                        : `${totalCount.toLocaleString()} rows`}
                    {' · '}{actorLabel}{' · '}{moment(startDate).format('MMM D')} – {moment(endDate).format('MMM D')}
                </p>
            )}

            {hasFetched && totalCount > 0 && (
                <div className={`reports-investigate-results${isStale ? ' reports-investigate-results--stale' : ''}`}>
                    <HistoryTable items={items} alwaysShowObject nullActorLabel="No Actor" />
                </div>
            )}

            {hasFetched && totalCount > 0 && showLoadMore && (
                <Button
                    color="secondary"
                    outline
                    className="reports-investigate-more"
                    disabled={loading || isStale}
                    onClick={handleLoadMore}
                >
                    Load {Math.min(pageSize, totalCount - items.length).toLocaleString()} more
                </Button>
            )}

            {hasFetched && !isStale && totalCount === 0 && (
                <div className="reports-investigate-stage">
                    <div className="reports-investigate-stage-icon">
                        <i className="fa fa-search" aria-hidden="true"></i>
                    </div>
                    <p className="reports-investigate-stage-title">
                        No history for {actorLabel} between {moment(startDate).format('MMM D')} and {moment(endDate).format('MMM D')}
                    </p>
                    <p className="reports-investigate-stage-text">
                        Try a wider date range{hasSelectedActions && ', or clear the action filter.'}
                    </p>
                    {hasSelectedActions && (
                        <Button
                            color="secondary"
                            outline
                            className="reports-investigate-clear-actions"
                            onClick={handleClearActions}
                        >
                            Clear actions
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

export default InvestigateTab
